<template>
    <Card ref="rootCard" style="height: 100%; max-width: 100%;" class="mac-chat relative h-full flex flex-col" :class="{ 'mac-chat-letter': letter }"
        @dragenter="onDragEnter" @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop"
        :pt="{
            root: letter ? { class: '!shadow-none !border-0 !bg-transparent' }
                : embedded ? { class: '!shadow-none !border-0' } : {},
            // Redesign (MYAC-1522): a letter has no card padding — the text lines up with its title.
            body: { class: letter ? 'flex flex-col flex-1 min-h-0 h-full !px-0 !pt-1' : 'flex flex-col flex-1 min-h-0 h-full' },
            content: { class: 'flex flex-col flex-1 min-h-0' },
            footer: { class: 'mt-auto' },
        }">
        <!-- Header (MYAC-882): capabilities help + labeled "New session" button — always
             available, so the user can abandon the current session and start fresh. -->
        <!-- Redesign: a letter-shaped chat has no tool bar — its host page carries
             the AI label and « Nouvelle session » on the letter's top line; saved
             prompts and the help sit above the message box (`letter-tools`). -->
        <template v-if="!letter" #title>
            <div class="flex flex-row items-center gap-1">
                <!-- MYAC-1313 (review 2026-09-14) — the chat is labelled here, leftmost
                     in the header bar, on every surface (Pilotage tab, chat page, docked
                     panel): the assistant's replies are model-authored. The label sits
                     on the chat, never inside a message — an opening reads exactly like
                     any other assistant turn. -->
                <AiGeneratedTag class="shrink-0 whitespace-nowrap" />
                <!-- MYAC-1327 — the chat's scope, shown once it HAS one: the project it
                     answers on, as a dropdown when there is more than one to switch
                     between. Making the first choice is not a header job — it is the
                     whole content of the empty state below, where the question is
                     asked and the answer sits directly under it. On project routes
                     nothing renders here — the in-project header is untouched.

                     `min-w-0` on the shrinkable items and `shrink-0` on the actions:
                     this row is tight. Once a conversation starts it also gains the
                     saved-prompts button, and without an explicit shrink order the
                     labels inside every control wrapped to two lines and the bar grew
                     a whole row (measured 35px → 56px in a 560px dock). The selector
                     is the one that gives way — it already truncates its own text. -->
                <template v-if="chatScopeMode === 'scoped'">
                    <Select v-if="scopeChoices.length > 1" :model-value="chatScopeId"
                        :options="scopeChoices" option-label="name" option-value="id"
                        size="small" class="min-w-28 max-w-48 shrink"
                        :aria-label="t('chat.scope.pick')"
                        @update:model-value="(id: string) => id && chatScopeSelect(id)" />
                    <span v-else-if="chatScopeProject" v-tooltip.bottom="t(scopeTooltipKey)"
                        class="inline-flex min-w-0 shrink">
                        <Tag :value="chatScopeProject.name" severity="secondary" class="max-w-48 !truncate" />
                    </span>
                </template>
                <div class="ml-auto flex flex-row items-center gap-1 shrink-0 whitespace-nowrap">
                <!-- MYAC-1269 — sharing: « Partager » / « Gérer le partage » for the
                     owner of a project conversation; for a participant, who sees it
                     and the way out. One control on the shared chat, so the dock and
                     the full page get it alike. -->
                <ConversationShareButton v-if="sharingAvailable" :compact="isCompact" />
                <Button icon="pi pi-question-circle" text size="small"
                    :aria-label="t('chat.capabilities-title')" v-tooltip.bottom="t('chat.capabilities-title')"
                    @click="showCapabilities = true" />
                <!-- MYAC-1106 — the saved-prompts button lives here, always (one control,
                     one place: savedPromptsBesideSession). Beside
                     « New session », with the other session tools (MYAC-1522,
                     Aymeric 2026-10-06). -->
                <SavedPromptsButton v-if="savedPromptsBesideSession" :compact="isCompact"
                    :prompts="savedPromptsStore.prompts" :load-failed="savedPromptsStore.loadFailed"
                    :has-loaded="savedPromptsStore.hasLoaded"
                    @use="useSavedPrompt" />
                <Button icon="pi pi-pen-to-square" :label="isCompact ? undefined : t('chat.new-session')"
                    text size="small" :aria-label="t('chat.new-session')"
                    v-tooltip.bottom="isCompact ? t('chat.new-session') : ''"
                    :disabled="chatbotStore.isCreatingSession" @click="handleNewChat" />
                </div>
            </div>
        </template>
        <template #content>
            <!-- Empty state: welcome only — the input lives in the footer at the bottom. -->
            <!-- `m-auto` on the child, NOT `justify-center` on the scroller: a
                 centred flex child that grows taller than its scroll container has
                 its TOP overflow clipped and unreachable — you can't scroll up to
                 it. Auto margins centre while it fits and collapse when it doesn't,
                 which is the whole fix. It bit hardest in the narrow docked panel,
                 where the welcome + prompt chips are tallest. -->
            <div v-if="hasNoMessages" class="flex flex-col h-full flex-1 overflow-y-auto overscroll-contain"
                :class="[opening ? 'items-start' : 'items-center', { 'chat-scroll-always': embedded && !letter, 'letter-inset': letter }]">
                <!-- MYAC-1313 — a pre-generated opening message (views/pilotage_opening)
                     REPLACES the generic welcome on surfaces that carry one: the page
                     opens on MAC having already said something, not on a title.
                     A chip send is a paid chat message — same credit gate as the
                     suggestion chips below ($$$ rule). -->
                <!-- Top-left, in the conversation's own grid (same p-3 as the message
                     list) — the opening IS turn zero, not a welcome card, so it sits
                     where the first assistant bubble of any conversation sits. -->
                <div v-if="opening" class="w-full flex flex-col items-start" :class="letter ? 'py-3 letter-measure' : 'p-3'">
                    <ChatOpening :message="opening.message" :replies="opening.replies"
                        :pending="opening.pending" :credits-blocked="isCreditsBlocked"
                        :assistant-name="assistantName" :avatar-icon="assistantAvatar"
                        @reply="r => r.run ? r.run() : !isCreditsBlocked && handleSend(r.label)" />
                    <!-- MYAC-1522 block 4 — what the host adds to its own opening (the
                         dashboard's points to look at), part of the same message. -->
                    <slot v-if="!opening.pending" name="opening-extra" />
                </div>
                <!-- MYAC-1327 — the account holds no projects at all: there is nothing
                     to pick and nothing to scope to. MYAC-1522 — what is offered is
                     the one first step of anyone with nothing to look at
                     (utils/firstStep.ts, the empty dashboard's EmptyProjectsState):
                     create what the plan allows for an admin, join what the company
                     already has, say it is not in the plan, or, for a member, which
                     admin can share. Never a create link a member cannot use (it
                     used to be « Créer un projet » → /qms for anyone). `bare`: the
                     chat panel is already the frame, and its narrowest width (340px)
                     holds the one column.
                     The input stays usable here and ONLY here — "you have no projects"
                     is simply true, so the server's no-scope framing is accurate, and a
                     brand-new member must still be able to ask what the platform is. -->
                <div v-else-if="chatScopeMode === 'create'" data-testid="chat-first-step"
                    class="m-auto p-4 w-full max-w-[26rem] flex flex-col gap-4">
                    <EmptyProjectsState :scope="null" bare />
                    <!-- MYAC-1348 — the welcome card is where the support chat is offered. -->
                    <SupportChatLink />
                </div>
                <!-- MYAC-1327 / MYAC-1522 — several projects to choose from and none
                     chosen: nothing is asked before the person writes (Aymeric 08/10,
                     the question up front confused more than it helped). The welcome
                     below stays; the question comes with the first message. -->
                <div v-else class="m-auto p-4 w-full max-w-2xl flex flex-col items-center gap-1">
                    <!-- MYAC-1522 block 4 — a host that has already greeted the person
                         (the dashboard's assistant column) skips the chat's own title. -->
                    <template v-if="!hostGreets">
                        <h2 class="text-2xl font-semibold text-center">{{ welcomeTitleText }}</h2>
                        <p v-if="welcomeSubtitleText" class="text-sm text-surface-500 text-center">{{ welcomeSubtitleText }}</p>
                    </template>
                    <SupportChatLink class="mt-2" />
                    <!-- MYAC-882 — a light sample of example prompts (one random pick per
                         capability family, re-rolled each new session); the full detailed
                         list lives in the capabilities popup. Self-contained prompts send
                         on click; prompts needing user input pre-fill the textbox. -->
                    <p class="text-sm text-surface-500 text-center mt-4 mb-1">{{ t('chat.suggestions-title') }}</p>
                    <!-- Prompts are whole sentences, and the docked panel is narrow.
                         PrimeVue's button label is `white-space: nowrap`, so a long
                         suggestion ran off the edge instead of wrapping — the text
                         was simply not readable until you widened the panel. Let the
                         label wrap and left-align it: a two-line chip reads fine, a
                         clipped one-line chip does not. -->
                    <div class="flex flex-wrap justify-center gap-2 w-full">
                        <Button v-for="s in randomSuggestions" :key="s.key" :label="t(s.promptKey)"
                            size="small" outlined rounded severity="secondary"
                            class="!max-w-full !text-left"
                            :pt="{ label: { class: '!whitespace-normal !leading-snug' } }"
                            :icon="s.action === 'prefill' ? 'pi pi-pencil' : undefined"
                            :disabled="isCreditsBlocked && s.action === 'send'"
                            @click="applySuggestion(s)" />
                    </div>
                    <Button :label="t('chat.capabilities-title')" link size="small" class="mt-1"
                        @click="showCapabilities = true" />
                </div>
                <!-- MYAC-1522 — the first message, sent while the chat has no subject:
                     it waits here, as the person's turn, under « Sur quel système
                     qualité on travaille ? »; choosing one sends it. -->
                <div v-if="heldMessage" data-testid="chat-held"
                    class="w-full flex flex-col gap-3" :class="letter ? 'py-3 letter-measure' : 'p-3'">
                    <div class="self-end max-w-[80%] rounded-xl bg-primary-50 px-4 py-2 whitespace-pre-line"
                        data-testid="chat-held-message">{{ heldMessage }}</div>
                    <ChatScopeLines :choices="scopeChoices" :tier="chatScopeTier" @select="sendHeld" />
                </div>
            </div>
            <!-- Messages: a dedicated height-bounded scroll section, so long
                 conversations scroll here and never overlap the input below. -->
            <!-- MYAC-1162 — the transcript and the open document share the width. The
                 split lives INSIDE the card's content, not around the card: this
                 component's single root is load-bearing (MYAC-882 — a second root
                 silently disables attribute fallthrough, and there is a test for it). -->
            <div v-else class="flex flex-1 min-h-0 gap-3">
            <!-- MYAC-1609 — once scrolled, the turns fade out under the top edge
                 instead of being cut by a line; at the very top nothing fades, so
                 the first line is never veiled. -->
            <div ref="scrollContainer" class="flex-1 min-h-0 overflow-y-auto overscroll-contain"
                :class="{ 'chat-scroll-always': embedded && !letter, 'letter-inset': letter, 'chat-fade-top': scrolledDown }"
                :style="{ '--chat-scrollbar-width': `${scrollbarWidth}px` }"
                data-scroll-bound @scroll.passive="updateVisibleSubject">
                        <!-- Normal message view -->
                        <!-- MYAC-1151 — trimmed 16px → 12px, not removed. The gap
                             BETWEEN turns is the one piece of whitespace here
                             that earns its keep: it is what separates a question
                             from its answer. -->
                        <!-- Redesign (MYAC-1522): more air between turns (gap-7), reading text in
                             .mac-chat (assets/main.css). MYAC-1609: no line touches the
                             column's edges — px-3 here, the same inset as the composer row;
                             a letter takes it from `.letter-inset` below. -->
                        <!-- MYAC-1269 — a letter starts closer to its header row (pt-2): the
                             title sat far below « Partager / Nouvelle session ». -->
                        <div v-if="!showRawEvents" ref="messagesContainer" class="flex flex-col gap-7 pb-5"
                            :class="letter ? 'letter-measure pt-2' : 'px-3 pt-5'">
                            <!-- Redesign (MYAC-1522) — what the host puts at the head of the
                                 transcript (the letter's folded title): it scrolls with the
                                 conversation instead of standing above it. -->
                            <slot name="lead" />
                            <!-- MYAC-1313 — the opening LINGERS as turn zero once the
                                 conversation starts: the message stays, its chips go —
                                 once something is sent they are spent (review 2026-09-14).
                                 MYAC-1522 (B3) — the one the first turn answered, when it
                                 was not this page's (a welcome letter's reply). -->
                            <ChatOpening v-if="lingeringOpening" :message="lingeringOpening.message"
                                :replies="lingeringOpening.replies"
                                :assistant-name="assistantName" :avatar-icon="assistantAvatar"
                                :credits-blocked="isCreditsBlocked"
                                :lingering="true"
                                @reply="r => r.run ? r.run() : !isCreditsBlocked && handleSend(r.label)" />
                            <slot v-if="lingeringIsHosts" name="opening-extra" />
                            <!-- MYAC-796: messages with persistent result cards anchored inline -->
                            <template v-for="item in messageTimeline" :key="item.key">
                                <!-- data-message-id: the header indicator reads WHICH turn is on
                                     screen off the DOM, and the timeline interleaves cards
                                     (results, question groups, file requests) between turns —
                                     so position in this list is not position in `messages`.
                                     The id is the join; cards carry none and are skipped. -->
                                <UserMessage v-if="item.kind === 'user'" :message="item.message"
                                    :data-message-id="item.message.id" />
                                <!-- MYAC-1519 — a project chat's own message on its way, or
                                     refused with its reason and a resend (a paid turn: the
                                     credit gate disables it, $$$ rule). -->
                                <div v-if="item.kind === 'user' && item.message.pending"
                                    data-testid="chat-message-sending"
                                    class="-mt-5 self-end text-xs text-surface-500">
                                    {{ t('chat.command.sending') }}
                                </div>
                                <div v-else-if="item.kind === 'user' && item.message.rejectedReasonKey"
                                    data-testid="chat-message-refused"
                                    class="-mt-5 self-end flex flex-row flex-wrap items-center justify-end gap-2 text-xs text-surface-600">
                                    <span>{{ t(item.message.rejectedReasonKey) }}</span>
                                    <!-- MYAC-1269 — a reader (downgraded after the refusal) may not resend. -->
                                    <span v-tooltip.top="chatbotStore.isReadOnlyConversation ? t('chat.shared.read_only_hint') : undefined"
                                        class="inline-flex">
                                        <Button :label="t('chat.command.resend')" icon="pi pi-refresh" size="small" text
                                            :disabled="isCreditsBlocked || chatbotStore.isLoading || chatbotStore.isStreaming
                                                || chatbotStore.isReadOnlyConversation"
                                            @click="handleResend(item.message.commandId)" />
                                    </span>
                                </div>
                                <ModelMessage v-else-if="item.kind === 'model'" :message="item.message"
                                    :data-message-id="item.message.id"
                                    :internal-link-handler="openInAppLink"
                                    :avatar-icon="assistantAvatar" :assistant-name="assistantName" />
                                <ActionResultCard v-else-if="item.kind === 'result'" :card="item.card" />
                                <QuestionCardsGroup v-else-if="item.kind === 'questions'" :group="item.group" />
                                <ActionProposalCard v-else-if="item.kind === 'settled'" :proposal="item.settled.proposal"
                                    :decided="item.settled.decision" />
                                <AuthoredDocumentCreatedCard v-else-if="item.kind === 'document'"
                                    :doc-id="item.card.docId" :title="item.card.title"
                                    @open="openDocument" />
                            </template>
                            <template v-if="chatbotStore.streamingMessage">
                                <ModelMessage :message="chatbotStore.streamingMessage" :avatar-icon="assistantAvatar"
                                    :internal-link-handler="openInAppLink"
                                    :assistant-name="assistantName" />
                            </template>
                            <p v-if="chatbotStore.turnNotice" data-testid="chat-turn-notice"
                                class="text-sm text-surface-600">{{ t(chatbotStore.turnNotice) }}</p>
                            <!-- THIS turn's approval cards appear only once the reply has
                                 finished streaming, below it — a card popping above a
                                 half-written sentence reads as broken and can't be clicked
                                 (actions are disabled mid-stream). Cards from PREVIOUS turns
                                 stay visible throughout: hiding them while an unrelated
                                 message streams would read as the pending action vanishing. -->
                            <ActionProposalCard v-for="p in visibleApprovalProposals" :key="p.proposal.proposal_id"
                                :proposal="p.proposal" :read-only="chatbotStore.isReadOnlyConversation" />
                            <!-- MYAC-883 — a finished refresh waits here for the user's review:
                                 the register is not written until they approve the delta.
                                 Firestore-driven (no session event), positioned below the
                                 streaming bubble — never hidden. -->
                            <RegisterDeltaCard v-for="proposal in pendingProposals" :key="proposal.id"
                                :proposal="proposal" />
                            <!-- MYAC-882 — never show the raw error payload (it stays in the
                                 console/monitoring logs): a generic sentence and a retry.
                                 Retrying sends a hidden follow-up so the user never retypes.
                                 Redesign (MYAC-1522) — a quiet line of the conversation,
                                 where the answer would have been, not a red box: the
                                 sentence and « Réessayer ». Writing a new message also
                                 clears it (the store drops the error on every send). -->
                            <p v-if="hasError" data-testid="chat-error"
                                class="answer-text m-0 px-1 flex flex-wrap items-baseline gap-x-2 text-surface-600" role="alert">
                                <span>{{ t(chatbotStore.errorMessageKey) }}</span>
                                <Button :label="t('chat.error-retry')" link size="small"
                                    data-testid="chat-error-retry"
                                    :loading="chatbotStore.isLoading || chatbotStore.isStreaming"
                                    :disabled="isCreditsBlocked"
                                    @click="handleRetry" />
                            </p>
                        </div>
                        <!-- Raw events view -->
                        <div v-else ref="rawEventsContainer" class="flex flex-col gap-4 p-4">
                            <div v-if="chatbotStore.isStreaming" class="text-xs text-surface-500 mb-2">
                                {{ t('chat.streaming') }}
                            </div>
                            <template v-for="(event, index) in chatbotStore.events" :key="event.id || index">
                                <div class="bg-surface-50 border border-surface-300 rounded p-3">
                                    <div class="text-xs text-surface-500 mb-2 font-semibold">
                                        {{ t('chat.event_label') }}{{ index + 1 }}<span v-if="event.id">{{ t('chat.event_id_prefix') }}{{ event.id }}{{ t('chat.event_id_suffix') }}</span>
                                    </div>
                                    <pre
                                        class="whitespace-pre-wrap text-surface-700 overflow-auto text-xs">{{ formatEvent(event) }}</pre>
                                </div>
                            </template>
                            <div v-if="chatbotStore.events.length === 0"
                                class="text-xs text-surface-500 text-center py-4">
                                {{ t('chat.no-events') }}
                            </div>
                        </div>
            </div>
            <AuthoredDocumentPanel v-if="panelDocId && panelProjectId" :project-id="panelProjectId"
                :doc-id="panelDocId" :detail-route="documentDetailRoute" :row="panelRow"
                :pending="panelPending" :error="panelError" @close="panelDocId = null" />
            </div>
        </template>
        <!-- Input Area — always at the bottom (welcome state included). -->
        <template #footer>
            <!-- Redesign (MYAC-1522): in a letter the whole footer — notices and
                 composer — keeps the letter's measure; elsewhere this wrapper is
                 layout-neutral (display: contents). -->
            <div :class="letter ? 'letter-measure letter-footer flex flex-col' : 'contents'">
            <!-- Debug info: Firestore path (only when debug enabled and session exists) -->
            <div v-if="appStore.debug && chatbotStore.sessionId"
                class="px-4 py-2 border-b border-surface-200 bg-surface-50">
                <div class="flex flex-row items-center justify-center gap-2">
                    <span class="text-xs text-surface-600 font-mono">{{ firestorePath }}</span>
                    <Button icon="pi pi-copy" size="small" text rounded :aria-label="t('chat.copy-path')"
                        v-tooltip.top="t('chat.copy-path')" @click="copyFirestorePath" />
                </div>
            </div>
            <div v-if="isCreditsBlocked"
                class="text-center text-sm text-orange-700 bg-orange-50 border-orange-200 px-4 py-2"
                :class="letter ? 'border rounded-lg mb-2' : 'border-b'">
                {{ t('chat.insufficient-credits') }}
            </div>
            <!-- MYAC-1327 — a scope is available but none is chosen: the input is
                 DISABLED WITH ITS REASON rather than hidden (frontend.md), and the
                 reason names the control that unblocks it. Answering here would mean
                 guessing which of the member's projects they mean.

                 Only once the conversation has content: on an empty session the
                 welcome block above already carries this exact sentence, and
                 printing it twice on one short panel read as a glitch. -->
            <!-- The control that unblocks it sits in the notice itself (MYAC-1522 block 4):
                 once the conversation has content the empty-state chooser is gone,
                 and the header's selector only exists once a scope does. Without
                 this the person was told to choose with nothing to choose with (a
                 message sent from the dashboard on a several-QMS account). -->
            <div v-else-if="chatAwaitingPick && !hasNoMessages" data-testid="chat-scope-blocked"
                class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-surface-600 bg-surface-100 border-surface-200 px-4 py-2"
                :class="letter ? 'border rounded-lg mb-2' : 'border-b'">
                <span>{{ t('chat.scope.pick-hint') }}</span>
                <Select :model-value="chatScopeId" data-testid="chat-scope-picker-held"
                    :options="scopeChoices" option-label="name" option-value="id"
                    size="small" class="min-w-48 max-w-full"
                    :placeholder="t('chat.scope.pick')" :aria-label="t('chat.scope.pick')"
                    @update:model-value="(id: string) => id && chatScopeSelect(id)" />
            </div>
            <!-- MYAC-919 — what the NEXT message is pointed AT. Visible because it
                 changes what the assistant answers about: an invisible attachment
                 that silently steers replies is the thing users report as "it keeps
                 talking about the wrong clause". It leaves with the message that
                 carries it, when the same «Discuss» button is pressed again, or
                 by the strip's own cross (MYAC-1531). Nothing here sends on its
                 own; the question chips do, on click.

                 The name is never truncated: a clause title clipped mid-word is
                 how you make a real requirement look like a tag. It wraps. -->
            <!-- What the answer ON SCREEN is about (MYAC-919 follow-up).
                 It follows the transcript, not the input: scroll back to an older
                 answer and this names ITS subject, so a reply can never be read
                 against the wrong clause. Absent when the visible turn carried no
                 subject — a plain question came from nothing in particular.

                 Moved out of the header (Aymeric, 2026-08-25): it shared one line
                 with two buttons in a 420px panel and truncated a real clause title
                 mid-word, which is how you make a requirement look like a tag. Down
                 here it has the row to itself and wraps like its sibling below.

                 Hidden while something is attached to the next message: the
                 focus strip names that subject in full, and two attachment chips
                 stacked over the input read as two attachments. -->
            <div v-if="indicatorFocus && !chatbotStore.pendingFocus"
                data-testid="chat-subject-indicator"
                class="mt-2 flex items-start gap-2 rounded-lg border border-primary-100 bg-primary-50/40 px-2 py-1.5" :class="letter ? 'mx-0' : 'mx-3'"
                v-tooltip.top="t('chat.focus.answering')">
                <i class="pi pi-paperclip text-primary text-xs shrink-0 mt-0.5" aria-hidden="true" />
                <span class="text-xs font-medium text-surface-700 break-words">{{ indicatorLabel }}</span>
            </div>
            <div v-if="chatbotStore.pendingFocus" data-testid="chat-focus-bar"
                class="mt-2 flex flex-col gap-1.5 rounded-lg border border-primary-100 bg-primary-50/50 px-2 py-1.5" :class="letter ? 'mx-0' : 'mx-3'">
                <div class="flex items-start gap-2">
                    <i class="pi pi-paperclip text-primary text-xs shrink-0 mt-0.5" aria-hidden="true" />
                    <span class="text-xs font-medium text-surface-700 break-words">
                        {{ focusLabel }}
                    </span>
                    <!-- MYAC-1531 — two controls, because a cross on an attachment
                         reads as "remove it": the chevron folds the questions and
                         leaves the subject attached, the cross detaches it. The
                         strip stays up folded, so both remain within reach. -->
                    <div class="ml-auto flex shrink-0 items-center gap-0.5">
                        <button v-if="focusSuggestions.length" type="button" data-testid="chat-focus-toggle"
                            class="rounded p-0.5 text-surface-400 transition-colors hover:bg-surface-200 hover:text-surface-600"
                            :aria-expanded="!suggestionsCollapsed"
                            :aria-label="t(suggestionsCollapsed ? 'chat.focus.show' : 'chat.focus.hide')"
                            v-tooltip.left="t(suggestionsCollapsed ? 'chat.focus.show' : 'chat.focus.hide')"
                            @click="suggestionsCollapsed = !suggestionsCollapsed">
                            <i class="pi text-[0.6rem]" :class="suggestionsCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'" />
                        </button>
                        <button type="button" data-testid="chat-focus-remove"
                            class="rounded p-0.5 text-surface-400 transition-colors hover:bg-surface-200 hover:text-surface-600"
                            :aria-label="t('chat.focus.remove')" v-tooltip.left="t('chat.focus.remove')"
                            @click="chatbotStore.clearFocus()">
                            <i class="pi pi-times text-[0.6rem]" />
                        </button>
                    </div>
                </div>
                <div v-if="focusSuggestions.length && !suggestionsCollapsed" class="flex flex-wrap items-center gap-1">
                    <Button v-for="key in focusSuggestions" :key="key" :label="t(key)"
                        size="small" outlined rounded severity="secondary"
                        class="!max-w-full !text-left !py-0.5"
                        :pt="{ label: { class: '!whitespace-normal !leading-snug !text-xs' } }"
                        :disabled="isCreditsBlocked || chatbotStore.isStreaming || chatbotStore.isLoading"
                        @click="handleSend(t(key))" />
                </div>
            </div>
            <!-- MYAC-1182 — pending attachments for the NEXT message, above the
                 input like the focus bar. Uploaded-in-a-previous-turn files show
                 no chip here: the transcript (UserMessage's summary) is their
                 record, this row is only "what is about to go out". -->
            <div v-if="attachmentsStore.pendingAttachments.length" data-testid="chat-attachment-chips"
                class="mt-2 flex flex-wrap gap-1.5" :class="letter ? 'mx-0' : 'mx-3'">
                <div v-for="a in attachmentsStore.pendingAttachments" :key="a.id"
                    class="flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs"
                    :class="a.error
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : 'border-surface-200 bg-surface-100 text-surface-700'">
                    <i :class="a.error ? 'pi pi-exclamation-triangle'
                        : (a.done ? attachmentIcon(a.family) : 'pi pi-spin pi-spinner')" aria-hidden="true" />
                    <span class="max-w-40 truncate" :title="a.filename">{{ a.filename }}</span>
                    <span class="text-surface-400">{{ formatFileSize(a.size) }}</span>
                    <span v-if="a.error" class="font-medium">{{ attachmentErrorText(a.error) }}</span>
                    <span v-else-if="!a.done">{{ t('chat.attachments.uploading') }}</span>
                    <button v-if="a.error === 'upload_failed'" type="button"
                        class="rounded p-0.5 text-surface-400 hover:bg-surface-200 hover:text-surface-600"
                        :aria-label="t('chat.attachments.retry')" v-tooltip.top="t('chat.attachments.retry')"
                        @click="retryAttachment(a.id)">
                        <i class="pi pi-refresh text-[0.65rem]" />
                    </button>
                    <button type="button"
                        class="rounded p-0.5 text-surface-400 hover:bg-surface-200 hover:text-surface-600"
                        :aria-label="t('chat.attachments.remove')" v-tooltip.top="t('chat.attachments.remove')"
                        @click="attachmentsStore.removeAttachment(a.id)">
                        <i class="pi pi-times text-[0.65rem]" />
                    </button>
                </div>
            </div>
            <!-- A letter has no tool bar: its saved prompts (once the exchange has
                 started, as in the bar) and the capabilities help go to the page's
                 top line, beside « Nouvelle session » (letterTools.ts); a page with
                 no such line keeps them right above the message box. -->
            <Teleport v-if="letter" :to="letterToolsTarget || 'body'" :disabled="!letterToolsTarget">
                <div class="flex flex-row items-center gap-1" :class="{ 'justify-end': !letterToolsTarget }"
                    data-testid="letter-tools">
                    <!-- MYAC-1269 — the share control goes where the letter's tools go:
                         a letter has no header bar, so without this line the
                         conversation page had no way to share. -->
                    <ConversationShareButton v-if="sharingAvailable" :compact="isCompact" />
                    <Button icon="pi pi-question-circle" text size="small" severity="secondary"
                        :aria-label="t('chat.capabilities-title')" v-tooltip.bottom="t('chat.capabilities-title')"
                        @click="showCapabilities = true" />
                    <SavedPromptsButton v-if="savedPromptsBesideSession" :compact="isCompact" severity="secondary"
                        :prompts="savedPromptsStore.prompts" :load-failed="savedPromptsStore.loadFailed"
                        :has-loaded="savedPromptsStore.hasLoaded"
                        @use="useSavedPrompt" />
                </div>
            </Teleport>
            <!-- MYAC-1269 — a reader of a shared conversation: « Partagée en lecture
                 seule » stands where the input would be. Not a disabled input: writing
                 is not temporarily impossible here, it is not theirs to do. -->
            <div v-if="chatbotStore.isReadOnlyConversation"
                class="flex flex-col items-center gap-0.5 py-3 text-center" :class="letter ? 'px-0' : 'px-3'"
                data-testid="chat-read-only">
                <span class="inline-flex items-center gap-2 text-sm font-medium text-surface-700">
                    <i class="pi pi-lock" aria-hidden="true" />{{ t('chat.shared.read_only') }}
                </span>
                <span class="text-xs text-surface-500">{{ t('chat.shared.read_only_hint') }}</span>
            </div>
            <div v-else class="flex flex-row justify-center items-center gap-2 py-1.5" :class="letter ? 'px-0' : 'px-3'">
                <span v-if="appStore.debug"
                    v-tooltip.top="showRawEvents ? t('chat.show-messages') : t('chat.show-raw-events')"
                    class="inline-flex">
                    <Button :icon="showRawEvents ? 'pi pi-eye-slash' : 'pi pi-code'" size="large" text
                        :aria-label="showRawEvents ? t('chat.show-messages') : t('chat.show-raw-events')"
                        @click="showRawEvents = !showRawEvents" />
                </span>
                <!-- MYAC-1519 — Stop the answer being written (a project chat runs on
                     the server, so it stops there, for every tab). MYAC-1609: it sits
                     in the box beside Send, drawn like it. -->
                <ChatInput v-model="inputMessage" :is-loading="chatbotStore.isLoading"
                    :is-streaming="chatbotStore.isStreaming"
                    :disabled="isCreditsBlocked || !!heldMessage" :send-blocked="attachmentsStore.blocksSend"
                    :attach-disabled="attachDisabled" :attach-disabled-reason="attachDisabledReason"
                    :placeholder="inputPlaceholder || undefined"
                    :stop="chatScopeId ? { enabled: chatbotStore.turnStoppable } : null"
                    @send="handleSend" @attach-files="handleAttachFiles" @stop="chatbotStore.stopTurn()"
                    wrapper-class="flex-1" />
            </div>
            <!-- MYAC-1369 — the drop zone is the whole card, so every surface that
                 mounts this component gets it. `pointer-events-none`: the drag events
                 keep landing on the card's own children and bubble to the handlers
                 on the Card, rather than flickering in and out on the overlay.
                 Blocked states say WHY (same reasons as the paperclip), never a
                 silent refusal. A polite status, not aria-hidden: a screen-reader user
                 dragging a file hears where it will land, and the QA bot reads it
                 off the accessibility tree. Lives in this slot to keep the component single-root
                 (see the Dialog note below). -->
            <div v-if="isDraggingFiles" data-testid="chat-drop-zone" role="status" aria-live="polite"
                class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 p-6 text-center rounded-xl border-2 border-dashed pointer-events-none bg-surface-0/95"
                :class="dropBlockedReason ? 'border-surface-300 text-surface-600' : 'border-primary text-primary'">
                <!-- Opaque backing for the message only: the faded transcript stays
                     visible as context without its text running through ours. -->
                <div class="flex flex-col items-center gap-2 px-5 py-4 rounded-xl bg-surface-0">
                    <i :class="dropBlockedReason ? 'pi pi-ban' : 'pi pi-upload'" class="text-2xl" />
                    <template v-if="dropBlockedReason">
                        <span class="text-sm font-medium">{{ dropBlockedReason }}</span>
                    </template>
                    <template v-else>
                        <span class="text-base font-semibold">{{ t('chat.attachments.drop-title') }}</span>
                        <span class="text-sm text-surface-600">{{ dropHint }}</span>
                    </template>
                </div>
            </div>

            <!-- MYAC-882 — "what can the assistant do" popup, built from the same single
                 source of truth as the suggestion chips (chatSuggestions.ts) so it never
                 drifts from the deployed agent's tools. Prompts are clickable here too
                 (usable mid-conversation). It lives INSIDE the Card slot (it teleports to
                 <body> anyway) to keep this component single-root — a second root node
                 disables attribute fallthrough, dropping the height/width the parent view
                 passes and collapsing the chat panel to its content size. -->
            <Dialog v-model:visible="showCapabilities" :header="t('chat.capabilities-title')" modal
                :dismissable-mask="true" class="w-full max-w-xl mx-4">
                <p class="text-sm text-surface-600 mb-3">{{ t('chat.capabilities-intro') }}</p>
                <div class="flex flex-col gap-4">
                    <div v-for="group in suggestionGroups" :key="group.key" class="flex flex-col gap-1">
                        <span class="text-sm font-semibold">{{ t(group.titleKey) }}</span>
                        <p class="text-sm text-surface-600">{{ t(group.descriptionKey) }}</p>
                        <div class="flex flex-wrap gap-2 mt-1">
                            <Button v-for="s in group.suggestions" :key="s.key" :label="t(s.promptKey)"
                                size="small" outlined rounded severity="secondary"
                                :icon="s.action === 'prefill' ? 'pi pi-pencil' : undefined"
                                :disabled="isCreditsBlocked && s.action === 'send'"
                                @click="applySuggestion(s)" />
                        </div>
                    </div>
                </div>
            </Dialog>
            </div>
        </template>
    </Card>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted, onBeforeUnmount, inject } from 'vue'
import { LETTER_TOOLS_TARGET } from '@/components/chat/letterTools'
import { useChatbotStore, type ChatFocusRef, type SettledProposal } from '@/stores/chatbot'
import {
    useChatAttachmentsStore,
    formatFileSize,
    type AttachmentErrorKind,
    type AttachmentFamily,
    MAX_ATTACHMENTS_PER_TURN,
    MAX_ATTACHMENT_BYTES,
} from '@/stores/chatAttachments'
import { focusSuggestionKeys, focusChipLabel } from '@/constants/chatFocusSuggestions'
import { pickVisibleIndex, subjectInForceAt } from '@/components/chat/visibleMessage'
import { useProjectsStore } from '@/stores/projects'
import { useChatPanelStore } from '@/stores/chatPanel'
import { useChatScope } from '@/composables/useChatScope'
import { useRouter } from 'vue-router'
import Select from 'primevue/select'
import Tag from 'primevue/tag'
import AuthoredDocumentCreatedCard from './AuthoredDocumentCreatedCard.vue'
import AuthoredDocumentPanel from './AuthoredDocumentPanel.vue'
import type { AuthoredDocumentCard } from '@/stores/chatbot'
import { useAuthoredDocument } from '@/composables/useAuthoredDocument'
import { useAppStore } from '@/stores/app'
import { useUsersStore } from '@/stores/users'
import { useFeaturesStore } from '@/stores/features'
import { useFeatureFlagsStore } from '@/stores/featureFlags'
import { logger } from '@/utils/logger'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { startNewConversation } from '@/composables/conversationMoves'
import { useI18n } from 'vue-i18n'
import UserMessage from './UserMessage.vue'
import ConversationShareButton from '@/components/conversations/ConversationShareButton.vue'
import ModelMessage from './ModelMessage.vue'
import { documentIdFromPath } from './inAppLink'
import ChatInput from './ChatInput.vue'
import ActionProposalCard from './ActionProposalCard.vue'
import ActionResultCard from './ActionResultCard.vue'
import QuestionCardsGroup from './QuestionCardsGroup.vue'
import EmptyProjectsState from '@/components/projects/EmptyProjectsState.vue'
import RegisterDeltaCard from './RegisterDeltaCard.vue'
import type { ChatbotMessage, ActionResultCard as ActionResultCardData, QuestionGroup } from '@/stores/chatbot'
import { useRegisterProposalsStore } from '@/stores/registerProposals'
import ChatOpening from '@/components/chat/ChatOpening.vue'
import ChatScopeLines from '@/components/chat/ChatScopeLines.vue'
import AiGeneratedTag from '@/components/shared/AiGeneratedTag.vue'
import { OPENING_PREFIX } from '@/types/pilotageOpening'
import {
    CHAT_SUGGESTION_GROUPS,
    pickOpeningSuggestions,
    type ChatSuggestion,
    type ChatSuggestionGroup,
} from '@/constants/chatSuggestions'
import SavedPromptsButton from '@/components/chat/SavedPromptsButton.vue'
import { useSavedPromptsStore } from '@/stores/savedPrompts'
import SupportChatLink from '@/components/chat/SupportChatLink.vue'
import { useSavedPromptPrefill } from '@/composables/useSavedPromptPrefill'
import type { SavedPrompt } from '@/models/SavedPrompt'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'

type TimelineItem =
    | { kind: 'user' | 'model'; key: string; message: ChatbotMessage }
    | { kind: 'result'; key: string; card: ActionResultCardData }
    | { kind: 'questions'; key: string; group: QuestionGroup }
    | { kind: 'document'; key: string; card: AuthoredDocumentCard }
    | { kind: 'settled'; key: string; settled: SettledProposal }

// Persona props — let a context-scoped chat (e.g. the CAPA assistant) humanize the
// surface with a name, a robot avatar and a custom welcome. All optional; defaults
// keep the generic chat look so other consumers are unaffected.
const props = withDefaults(defineProps<{
    assistantName?: string
    assistantAvatar?: string
    welcomeTitle?: string
    welcomeSubtitle?: string
    /** Optional input placeholder override (e.g. context-specific example prompts). */
    inputPlaceholder?: string
    /**
     * Optional ordered allow-list of suggestion group keys (MYAC-835). When set,
     * only these groups show, in this order — e.g. the Product-tab chat leads with
     * `technical_documentation` and drops the QMS-only register/assessment groups.
     * Unset → all groups in their default order.
     */
    suggestionGroupKeys?: string[]
    /**
     * MYAC-1329 — narrows the OPENING chips only, leaving the capabilities popup on
     * `suggestionGroupKeys`' set: the QMS chat opens on its own families but can
     * still show everything it does. See `chatPageSuggestionProps`.
     */
    openingGroupKeys?: string[]
    /**
     * MYAC-1313 — a pre-generated opening message that replaces the empty-state
     * welcome: MAC's first word, read from a projected view and NEVER generated
     * at render. Strings arrive locale-resolved.
     */
    opening?: {
        message: string
        /** A chip sends exactly its label — what it shows is what it sends. */
        replies: { label: string, kind: string, run?: () => void }[]
        eventAt?: Date | null
        /** The view has not answered yet: a skeleton holds the place, never the generic welcome. */
        pending?: boolean
    } | null
    /**
     * MYAC-1313 — the chat is embedded in a host page (the Pilotage Chat tab): no
     * card of its own (the host frames it); its scrollbar always shows, a rail from
     * the header down to the message box that ties the conversation to it (review
     * 2026-09-14). The box stays pinned at the bottom; messages read top-down.
     */
    embedded?: boolean
    /**
     * Redesign (MYAC-1522) — the conversation is written as a letter (the Pilotage
     * page): no card, larger prose on a reading measure, notices as tiles, the
     * person's turns as a light tile on the right. Styles: `.mac-chat-letter` in
     * assets/main.css.
     */
    letter?: boolean
    /**
     * Redesign (MYAC-1522, block 4) — the host page has already greeted the person
     * (the dashboard's assistant column): the empty state skips its own « Bonjour,
     * je suis MAC » title and goes straight to what is asked of them.
     */
    hostGreets?: boolean
    /**
     * MYAC-1522 (B3) — the host decides the opening shown above a running
     * conversation, whatever its first turn carried: the dashboard's column shows
     * the opening of the conversation's project once it runs (Aymeric 08/10), never
     * the portfolio sentence a conversation started there answered.
     */
    hostOpeningWins?: boolean
    /**
     * Show an opened document beside the conversation (MYAC-1162). Off by default:
     * the docked panel is a few hundred pixels wide, and half of that is not a
     * document. The full-page chats turn it on; everywhere else the card's Open
     * navigates to the document page instead.
     */
    documentPanel?: boolean
}>(), {
    assistantName: '',
    assistantAvatar: '',
    welcomeTitle: '',
    welcomeSubtitle: '',
    opening: null,
    embedded: false,
    letter: false,
    hostGreets: false,
    hostOpeningWins: false,
    inputPlaceholder: '',
    suggestionGroupKeys: undefined,
    openingGroupKeys: undefined,
})

const chatbotStore = useChatbotStore()
const attachmentsStore = useChatAttachmentsStore()
const projectsStore = useProjectsStore()
// MYAC-1327 — the chat's own scope: the route's project inside one, the
// account's QMS on global surfaces (dashboard). Every project-bound behaviour
// of this component (session, attachments, documents, proposals) reads THIS,
// never the sticky currentProjectId.
const {
    scopeProjectId: chatScopeId, scopeMode: chatScopeMode,
    scopeProject: chatScopeProject, scopeOptions: chatScopeOptions,
    scopeTier: chatScopeTier, isAwaitingPick: chatAwaitingPick,
    selectScope: chatScopeSelect,
} = useChatScope()

// MYAC-1327 — the ASK is worded generically ("choose a project"), because what the
// list holds depends on the account: a QMS, a product, or a regulatory watch. Naming
// one of them in the prompt is wrong for the other two, and the options themselves
// already say what they are.
//
// The tooltip on a RESOLVED scope stays tier-specific: there the type is known, and
// it is the useful half of the sentence ("answering on this QMS and its linked
// products"). Explicit per tier — an `else` branch would mislabel any project type a
// future build adds (PR #751 review).
/**
 * What the picker renders: id + name, nothing else.
 *
 * `scopeOptions` holds `Project` MODELS — class instances carrying datasources,
 * scheduling maps, timestamps. Handing those to the dropdown made opening it cost
 * ~400ms of blocked main thread on an account with 23 of them, against ~10ms for
 * every other dropdown in the app (measured, 2026-09-18): the list renders each
 * option through reactive proxies over the whole object graph. Two plain strings
 * are all the control ever needed.
 */
const scopeChoices = computed(() => chatScopeOptions.value.map(p => ({ id: p.id, name: p.name })))

const scopeTooltipKey = computed(() => chatScopeTier.value === 'td' ? 'chat.scope.td-tooltip'
    : chatScopeTier.value === 'regulatory_monitoring' ? 'chat.scope.regmon-tooltip'
        : 'chat.scope.qms-tooltip')
const appStore = useAppStore()
const usersStore = useUsersStore()
const featuresStore = useFeaturesStore()
const { t } = useI18n()

// Draft lives in the store so it survives the drawer closing/reopening (MYAC-750).
const inputMessage = computed({
    get: () => chatbotStore.draftMessage,
    set: (v: string) => { chatbotStore.draftMessage = v },
})

// MYAC-1106 — saved prompts: per-project, so only when the chat is project-scoped.
const savedPromptsStore = useSavedPromptsStore()
const { prefill: prefillSavedPrompt } = useSavedPromptPrefill()
const hasSavedPromptsScope = computed(() => !!chatScopeId.value)
/**
 * The saved prompts sit beside « New session », always, before the first message as
 * after it (MYAC-1522, Aymeric 2026-10-06). They used to start in the welcome block
 * and move to the header once the conversation had started, so a fresh chat whose
 * opening replaced that block had them nowhere.
 */
const savedPromptsBesideSession = computed(() => hasSavedPromptsScope.value)
const useSavedPrompt = (p: SavedPrompt) => {
    prefillSavedPrompt(p)
    // ChatInput does not expose a focus() (no chatInputRef here) — skipped; see task-9 brief.
}

// MYAC-796 — messages with result cards interleaved at their anchor message, so a card
// stays fixed in the conversation history instead of floating at the bottom.
const messageTimeline = computed<TimelineItem[]>(() => {
    const cardsByAnchor = new Map<string, ActionResultCardData[]>()
    const trailing: ActionResultCardData[] = []
    const messageIds = new Set(chatbotStore.messages.map(m => m.id))
    for (const card of chatbotStore.resultCards) {
        if (card.afterMessageId && messageIds.has(card.afterMessageId)) {
            const arr = cardsByAnchor.get(card.afterMessageId)
            if (arr) arr.push(card)
            else cardsByAnchor.set(card.afterMessageId, [card])
        } else {
            trailing.push(card) // no/unknown anchor → after all messages
        }
    }
    // MYAC-802 — question card groups are anchored by EVENT order. MYAC-1152 — the
    // anchor is the TURN: a group renders after every message of the turn that raised
    // it and before the user's answer to it. Unanchored groups trail at the bottom.
    const eventIndexById = new Map(chatbotStore.events.map((e, i) => [e.id, i]))
    type Anchored = { eventIndex: number; group: QuestionGroup }
    const pendingGroups: Anchored[] = chatbotStore.questionGroups
        .map(g => ({ eventIndex: g.eventIndex, group: g }))
        .sort((a, b) => a.eventIndex - b.eventIndex)
    const toItem = (a: Anchored): TimelineItem =>
        ({ kind: 'questions', key: `q-${a.group.group.group_id}`, group: a.group })
    // MYAC-1162 — a document card belongs to the turn that opened the document, and
    // rides exactly the question-group rule: flushed under the model's report of that
    // turn and above the user's next message, so it stays put as the conversation
    // grows instead of floating to the bottom.
    const pendingDocs: AuthoredDocumentCard[] = [...chatbotStore.authoredDocumentCards]
        .sort((a, b) => a.eventIndex - b.eventIndex)
    const toDocItem = (c: AuthoredDocumentCard): TimelineItem =>
        ({ kind: 'document', key: `d-${c.docId}`, card: c })
    // MYAC-1269 — a card someone else settled stays where it was raised, with
    // their name on it, under the same turn rule as a document card.
    const pendingSettled: SettledProposal[] = [...chatbotStore.settledProposals]
        .sort((a, b) => a.eventIndex - b.eventIndex)
    const toSettledItem = (p: SettledProposal): TimelineItem =>
        ({ kind: 'settled', key: `s-${p.proposal.proposal_id}`, settled: p })
    const items: TimelineItem[] = []
    for (const m of chatbotStore.messages) {
        const mIndex = eventIndexById.get(m.id) ?? -1
        // MYAC-1152 — a card belongs to the turn that raised it, so it is flushed
        // just BEFORE the user's next message: under the model's report, above the
        // answer given to it. Flushing after the message instead put every card of a
        // multi-step conversation below the answer it had already received, and put
        // it there whichever shape the turn took (report on the call event, or a
        // lead-in sentence emitted after the card's own response event).
        if (m.isUserMessage) {
            while (pendingGroups.length && mIndex >= 0 && pendingGroups[0].eventIndex < mIndex) {
                items.push(toItem(pendingGroups.shift()!))
            }
            while (pendingDocs.length && mIndex >= 0 && pendingDocs[0].eventIndex < mIndex) {
                items.push(toDocItem(pendingDocs.shift()!))
            }
            while (pendingSettled.length && mIndex >= 0 && pendingSettled[0].eventIndex < mIndex) {
                items.push(toSettledItem(pendingSettled.shift()!))
            }
        }
        items.push({ kind: m.isUserMessage ? 'user' : 'model', key: `m-${m.id}`, message: m })
        for (const c of cardsByAnchor.get(m.id) ?? []) {
            items.push({ kind: 'result', key: `c-${c.id}`, card: c })
        }
    }
    // Unanchored groups are THIS turn's — their lead-in text is still streaming.
    // Hold them until the reply lands in messages[], where they anchor beneath it.
    if (!chatbotStore.isStreaming && !chatbotStore.isLoading) {
        for (const g of pendingGroups) items.push(toItem(g))
        for (const c of pendingDocs) items.push(toDocItem(c))
        for (const p of pendingSettled) items.push(toSettledItem(p))
    }
    for (const c of trailing) items.push({ kind: 'result', key: `c-${c.id}`, card: c })
    return items
})

// MYAC-1179 — the empty chat opens with MAC introducing itself, so the user
// knows WHO answers before typing. Two sentences, two facts: the name, and that
// the thing is ready — the copy rule's ceiling, and why nothing else was added.
//
// The name resolves through the persona prop first: a context-scoped chat that
// passes `assistantName` greets under ITS name, and only the main chat falls
// back to MAC's. Keeping "MAC" in one locale key (not baked into the
// greeting sentences) is what made MYAC-1102's rename a one-line change; the
// next rename should stay one too.
const assistantDisplayName = computed(() => props.assistantName || t('chat.assistant-name'))
const welcomeTitleText = computed(() =>
    props.welcomeTitle || t('chat.welcome-title', { name: assistantDisplayName.value }))
// The default second line belongs to the DEFAULT greeting only. A persona that
// wrote its own title said what it wanted to say; appending "How can I help you
// today?" underneath would put words in its mouth it never asked for.
const welcomeSubtitleText = computed(() =>
    props.welcomeSubtitle || (props.welcomeTitle ? '' : t('chat.welcome-subtitle')))
const messagesContainer = ref<HTMLElement | null>(null)
const rawEventsContainer = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const showRawEvents = ref(false)

/**
 * MYAC-1162 — tell the host when a document sits beside the conversation.
 *
 * The full-page chat is deliberately narrow (a column of prose reads badly at full
 * viewport width), but half of that narrow column is not a document. The host widens
 * while the panel is open and returns to its reading width when it closes; this
 * component does not reach up into its own container to do it.
 */
const emit = defineEmits<{ 'document-open': [open: boolean] }>()

// ── MYAC-1162: the document open beside the conversation ─────────────────────
const router = useRouter()
const panelDocId = ref<string | null>(null)
const panelProjectId = computed(() => chatScopeId.value ?? '')
/**
 * Which shell's document page this is.
 *
 * Keyed on the PROJECT'S OWN TYPE, not on the URL's shape: that is the fact the router
 * itself keys on (`projectRouteBase`), it cannot be fooled by a path, and it answers
 * for a host that is not a project route at all — the dock, which floats over any
 * page. A regulatory-monitoring project has no document page, so it resolves to
 * nothing rather than to a guess, and both the "open the full page" link and the
 * navigation fall away with it.
 */
const documentDetailRoute = computed(() => {
    switch (chatScopeProject.value?.type) {
        case 'qms':
            return 'qms-document'
        case 'td':
            return 'products-document'
        default:
            return undefined
    }
})

/**
 * Open a document: beside the conversation where there is room, otherwise on its own
 * page. Both are "open" from the user's side; only one of them fits in a dock.
 */
function openDocument(docId: string): void {
    if (props.documentPanel) {
        panelDocId.value = docId
        return
    }
    if (!panelProjectId.value || !router || !documentDetailRoute.value) return
    // The conversation follows the member to the document (review 2026-09-14): open
    // the dock beside it where there is room for one. A no-op where the dock is
    // already open (it is itself a host) and on the dedicated chat pages (the store
    // holds it off there). A navigation side effect, not a "chat opened" gesture,
    // so it is not counted. Read here, not at setup: only this branch needs it.
    const chatPanel = useChatPanelStore()
    if (chatPanel.regime === 'docked') chatPanel.open({ userInitiated: false })
    router.push({
        name: documentDetailRoute.value,
        params: { pid: panelProjectId.value, docId },
    })
}

/**
 * The assistant writes links to documents in its own sentences, and they used to
 * open a new browser tab — where the app boots from scratch and the conversation
 * is simply gone, because a session is not rehydrated (Aymeric: "the link in the
 * chat opens a new page and we lose the chat context").
 *
 * Here the document opens BESIDE the conversation where there is a panel for it;
 * elsewhere it opens on its own page with the dock beside it — the same path as a
 * card's Open (`openDocument`), so a link and a card never disagree.
 */
function openInAppLink(path: string): boolean {
    if (!router) return false
    const docId = documentIdFromPath(router, path)
    if (!docId) return false
    openDocument(docId)
    return true
}

/**
 * A document the assistant OPENS auto-opens the panel; one that merely ARRIVES does
 * not.
 *
 * `userSentSomething` is the discriminator: this component's own send handler sets it,
 * so nothing opens itself before the user has asked for anything in this session.
 *
 * The original rationale here was replay — that reopening an old conversation would
 * fling open a document written last week. That was wrong about this store, which
 * never GETs a stored session; a reload starts a fresh conversation. The gate is kept
 * because it is still the correct rule for the ONE surface a document can arrive on
 * without the user asking (a card already present when this component mounts), and it
 * costs a boolean.
 */
const userSentSomething = ref(false)
const seenDocIds = new Set<string>()

watch(
    () => chatbotStore.authoredDocumentCards.map(c => c.docId).join('|'),
    () => {
        const cards = chatbotStore.authoredDocumentCards
        if (cards.length === 0) {
            // New session: forget what was open, or the panel outlives its conversation.
            seenDocIds.clear()
            panelDocId.value = null
            return
        }
        const fresh = cards.filter(c => !seenDocIds.has(c.docId))
        for (const c of fresh) seenDocIds.add(c.docId)
        if (!fresh.length || !userSentSomething.value || !props.documentPanel) return
        panelDocId.value = fresh[fresh.length - 1].docId
    },
    { immediate: true },
)

// The live read for whatever is open. An empty docId subscribes to nothing, so this
// costs a listener only while a document is actually beside the conversation.
const { data: panelRow, pending: panelPending, error: panelError } = useAuthoredDocument(
    panelProjectId,
    () => panelDocId.value ?? '',
)

watch(panelDocId, docId => emit('document-open', docId !== null))

const hasNoMessages = computed(() => {
    return chatbotStore.messages.length === 0 && !chatbotStore.streamingMessage && !chatbotStore.error
})

const hasError = computed(() => {
    return chatbotStore.error !== null
})

const isCreditsBlocked = computed(() => featuresStore.isCreditsBlocked)
// MYAC-1269 — sharing rides the conversations flag (`enable_conversations`): a
// shared conversation is found through the lists, so without them a share would
// be an invitation nobody can follow. Off on prod until the lists open there.
const featureFlagsStore = useFeatureFlagsStore()
const sharingAvailable = computed(() =>
    featureFlagsStore.isConversationsEnabled && chatbotStore.projectMode && !!chatbotStore.sessionId)

/**
 * MYAC-1522 (B3) — the opening shown above a running conversation: the one its
 * first turn carried (`chatbotStore.conversationOpening`), when that is not this
 * page's own; else this page's, as before. A welcome letter's reply lands on a
 * project chat whose own opening is its Pilotage summary: the person must see the
 * letter they answered, not a text the agent never had. Its chips are spent.
 */
const lingeringIsHosts = computed(() => {
    const carried = chatbotStore.conversationOpening
    return Boolean(props.opening && !props.opening.pending
        && (props.hostOpeningWins || !carried || carried.message === props.opening.message))
})
const lingeringOpening = computed<{ message: string, replies: { label: string, kind: string }[] } | null>(() => {
    if (lingeringIsHosts.value) return { message: props.opening!.message, replies: props.opening!.replies }
    const carried = chatbotStore.conversationOpening
    return carried && !props.hostOpeningWins ? { message: carried.message, replies: [] } : null
})

/**
 * MYAC-1327 — below this width the header's two labelled buttons go icon-only.
 *
 * The bar carries the AI label, the scope, the saved prompts and "New session";
 * in French those labels alone want ~600px and the docked panel gives ~500. The
 * first attempt let the flex row shrink whatever it liked, and it ate the scope
 * selector down to 2px — the chat stopped naming the project it answers on,
 * which is the whole point of this ticket. So the SCOPE keeps a floor
 * (`min-w-28`) and the labels are what give way; the names stay reachable in the
 * tooltips and the aria-labels.
 *
 * Measured on the host rather than read off the chat-panel store: this component
 * also renders on the full-width chat pages, which know nothing about the dock.
 * The threshold sits just under those pages (measured 614px at a 1280px viewport)
 * and just over the docked panel's default 560px, so the page keeps its labels
 * and the dock gives them up — and a genuinely narrow window compacts both,
 * which is what should happen.
 */
const COMPACT_HEADER_WIDTH = 600
const rootCard = ref<{ $el?: HTMLElement } | null>(null)
const hostWidth = ref(Number.POSITIVE_INFINITY)
const isCompact = computed(() => hostWidth.value < COMPACT_HEADER_WIDTH)
let hostObserver: ResizeObserver | null = null

// MYAC-919 — the attached object: its chip label and the questions offered for
// it. Read from the store (not a prop) because the attachment follows the
// CONVERSATION, not the mount point — the same chip belongs to the panel and to
// the full page, which are two views of one session.
const focusLabel = computed(() =>
    chatbotStore.pendingFocus ? focusChipLabel(chatbotStore.pendingFocus) : '')

// Folding the questions leaves the subject attached; only the cross detaches it
// (MYAC-1531). Ephemeral (no persistence) — a new subject is a new offer, and
// permanently folded questions are a discoverability feature that deletes
// itself the first time someone is busy.
const suggestionsCollapsed = ref(false)
watch(() => chatbotStore.pendingFocus, () => (suggestionsCollapsed.value = false))

// --- Which subject the visible part of the conversation is about -------------
// Read off the transcript rather than tracked separately: every user event still
// carries its own marker, so scrolling is the only thing that has to be watched.
const visibleFocus = ref<ChatFocusRef | null>(null)
const atBottom = ref(true)
// MYAC-1609 — the top fade: only once the transcript has moved, and never over
// the scrollbar (its width is excluded from the mask).
const scrolledDown = ref(false)
const scrollbarWidth = ref(0)

const updateVisibleSubject = () => {
    const scroller = scrollContainer.value
    const list = messagesContainer.value
    if (!scroller || !list) {
        // No transcript on screen yet (empty chat): whatever is attached to the
        // input is the subject, and there is nothing to read back.
        atBottom.value = true
        visibleFocus.value = null
        scrolledDown.value = false
        return
    }
    scrolledDown.value = scroller.scrollTop > 0
    scrollbarWidth.value = scroller.offsetWidth - scroller.clientWidth
    // "At the bottom" is where the next message will land, so the input's own
    // pending subject wins there; anywhere else the transcript does.
    atBottom.value = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80
    const base = scroller.getBoundingClientRect().top
    // Only the elements that ARE turns take part: a result card or a question
    // group sits between them and would otherwise shift every index after it.
    const ids: string[] = []
    const tops: number[] = []
    for (const el of Array.from(list.children)) {
        const id = (el as HTMLElement).dataset.messageId
        if (!id) continue
        ids.push(id)
        tops.push(el.getBoundingClientRect().top - base)
    }
    const messages = chatbotStore.messages
    const visibleId = ids[pickVisibleIndex(tops)]
    const index = messages.findIndex(m => m.id === visibleId)
    visibleFocus.value = subjectInForceAt(messages, index === -1 ? messages.length - 1 : index)
}

/**
 * What the header names. At the bottom that is the subject of the message about
 * to be sent (or of the last one); scrolled up it is whatever the answer under
 * the reader's eyes was asked about — including nothing, when that question
 * carried no subject.
 */
/**
 * What the header names: the subject IN FORCE where the reader is — the last one
 * attached at or before that point, not only the one this exact turn carried
 * (Aymeric, 2026-08-19: "it should linger until subject change"). A follow-up
 * asked without re-attaching is still about the same thing, and blanking the
 * badge on it read as losing the thread.
 */
const indicatorFocus = computed<ChatFocusRef | null>(() => {
    if (!atBottom.value) return visibleFocus.value
    // Pinned to the bottom, the reader is on the NEWEST turn — not on whatever
    // happens to be topmost in the viewport, which is what `visibleFocus` tracks
    // (a long answer can fill the screen while its own question is far above).
    const messages = chatbotStore.messages
    return chatbotStore.pendingFocus ?? subjectInForceAt(messages, messages.length - 1)
})

const indicatorLabel = computed(() =>
    indicatorFocus.value ? focusChipLabel(indicatorFocus.value) : '')
const focusSuggestions = computed(() =>
    chatbotStore.pendingFocus ? focusSuggestionKeys(chatbotStore.pendingFocus) : [])

const formatEvent = (event: unknown): string => {
    return JSON.stringify(event, null, 2)
}

const firestorePath = computed(() => {
    const uid = usersStore.user?.uid
    const sessionId = chatbotStore.sessionId
    if (!uid || !sessionId) {
        return ''
    }
    return `users/${uid}/chats/${sessionId}`
})

const copyFirestorePath = async () => {
    const path = firestorePath.value
    if (!path) {
        return
    }
    try {
        await navigator.clipboard.writeText(path)
        appStore.toast(
            t('chat.path-copied-title'),
            t('chat.path-copied-detail', { path }),
            'success',
            3000
        )
    } catch (error) {
        logger.error('Failed to copy Firestore path:', error)
        appStore.toast(
            t('chat.path-copy-failed-title'),
            t('chat.path-copy-failed-detail'),
            'error',
            3000
        )
    }
}

/** MYAC-1522 — the first message, waiting for the person to say which project it is about. */
const heldMessage = ref<string | null>(null)
/** A project was chosen under it (as opposed to the wait ending some other way). */
const heldPicked = ref(false)

/** The project chosen under a held message: it becomes the subject; the watcher below sends. */
function sendHeld(id: string): void {
    if (isCreditsBlocked.value) return
    chatScopeSelect(id)
    // Only a choice that took: a project no longer offered is a silent no-op in
    // the scope, and must not count as picked (PR 911 review).
    if (!chatAwaitingPick.value) heldPicked.value = true
}

// The held message goes only after a real choice, once the chat is free and with
// credits left. If the wait ends otherwise — walking into a project (the dock
// stays mounted), credits running out — the text goes back into the box: never
// an unscoped turn, never a message lost (PR 911 review).
watch(
    () => [heldMessage.value, chatAwaitingPick.value, chatbotStore.isLoading || chatbotStore.isStreaming, isCreditsBlocked.value] as const,
    ([held, awaiting, busy, blocked]) => {
        if (!held) return
        if (awaiting && !blocked) return
        if (!heldPicked.value || blocked) {
            inputMessage.value = held
            heldMessage.value = null
            heldPicked.value = false
            return
        }
        if (busy) return
        heldMessage.value = null
        heldPicked.value = false
        void handleSend(held)
    },
)

const handleSend = async (message?: string) => {
    // Get message from event parameter or fallback to input value
    const messageToSend = message || inputMessage.value.trim()

    if (!messageToSend || chatbotStore.isLoading || chatbotStore.isStreaming) {
        return
    }
    // No subject yet among several: answering would mean guessing
    // (dashboard-chat-scope.md step 5). Whatever path asked to send — the box, a
    // reply chip, a handed-over message — the message waits as the person's turn
    // and the chat asks which project; choosing one sends it (MYAC-1522).
    if (chatAwaitingPick.value) {
        // A conversation already on screen keeps the notice + chooser above the
        // box (chat-scope-blocked): the text waits in the box there.
        if (!hasNoMessages.value) {
            inputMessage.value = messageToSend
            return
        }
        heldMessage.value = messageToSend
        if (inputMessage.value.trim() === messageToSend) inputMessage.value = ''
        return
    }
    // From here on, a document card that appears is THIS user's doing (see the
    // auto-open watcher) rather than a session being replayed.
    userSentSomething.value = true

    // MYAC-1313 — the FIRST turn from a surface with a pre-generated opening
    // carries what MAC "just said", so a bare "no" answers the question that
    // was actually asked. Same channel as __FOCUS__ (client session state is
    // 403'd server-side); one marker per turn, so a message the store will
    // focus-prefix is left alone — focus wins, the reply chips' prompts are
    // self-contained anyway.
    //
    // "Focus wins" has to be CHECKED here, not assumed: the store prefixes the
    // focus marker inside `sendMessage`, i.e. AFTER this line, so without the
    // guard both markers went out. Seen on dev 2026-10-13 (MYAC-1166): the task
    // dialog's « ask the assistant to draft it » opened a fresh session, and
    // the agent was told the user was "answering the opening message … in its
    // warm tone" about a task they had just pointed at — it chatted instead of
    // proposing.
    //
    // A surface that handed the message over may say what MAC « just said » there
    // (`pendingOpening`, the welcome letter, MYAC-1522 B3): it answers that, not
    // this chat's own opening. Consumed by this turn whatever it carries.
    const handed = chatbotStore.pendingOpening
    chatbotStore.pendingOpening = null
    const opening = handed ?? (props.opening && !props.opening.pending ? props.opening : null)
    const withOpening = opening && hasNoMessages.value && !chatbotStore.pendingFocus
        ? `${OPENING_PREFIX}${JSON.stringify({ message: opening.message })}\n${messageToSend}`
        : messageToSend

    inputMessage.value = ''
    await chatbotStore.sendMessage(withOpening)

    // Auto-scroll to bottom after message (only when messages exist)
    if (!hasNoMessages.value) {
        await nextTick()
        scrollToBottom()
    }
}

// MYAC-1522 block 4 — a message handed over by another surface
// (chatPanel.openAndSend). Waits for the opening to land, so the first turn still
// carries what MAC « just said »; out of credits it becomes the draft, never a
// paid turn ($$$ rule), and while the chat asks for its subject handleSend keeps
// it as the draft too. While an answer is still coming it waits its turn rather
// than being dropped by handleSend's busy check.
watch(
    () => [chatbotStore.pendingSend, props.opening?.pending, chatbotStore.isLoading || chatbotStore.isStreaming] as const,
    ([text, openingPending, busy]) => {
        // A handed-over opening is already here: no need to wait for this chat's.
        if (!text || (openingPending && !chatbotStore.pendingOpening) || busy) return
        chatbotStore.pendingSend = null
        if (isCreditsBlocked.value) {
            chatbotStore.pendingOpening = null
            inputMessage.value = text
            return
        }
        void handleSend(text)
    },
    { immediate: true },
)

// MYAC-1182 — the paperclip needs a project (attachments live at
// `projects/{pid}/chat_uploads/…`, spec §1). MYAC-1327: the project is the
// CHAT'S scope — on the dashboard that is the QMS, so uploads land on the QMS
// project's own shelf, exactly as in the in-QMS chat. Disabled-with-reason
// when there is no scope, not hidden (frontend.md).
const attachDisabled = computed(() => !chatScopeId.value)
const attachDisabledReason = computed(() => {
    if (chatScopeMode.value === 'create') return t('chat.scope.attach-no-project')
    if (chatScopeMode.value === 'pick') return t('chat.scope.attach-pick-first')
    return t('chat.attachments.no-project')
})

// MYAC-1398 — one message per refusal, naming what was refused and the limit or
// the formats to use instead; the limits come from the store's constants.
const attachmentErrorText = (kind: AttachmentErrorKind): string => t(`chat.attachments.errors.${kind}`, {
    n: MAX_ATTACHMENTS_PER_TURN,
    mb: Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024)),
})
// The chip's icon says the family at a glance (PrimeIcons has word / excel / pdf;
// the rest share the plain file).
const attachmentIcon = (family: AttachmentFamily | null): string => {
    switch (family) {
        case 'pdf': return 'pi pi-file-pdf'
        case 'word': return 'pi pi-file-word'
        case 'excel': return 'pi pi-file-excel'
        default: return 'pi pi-file'
    }
}

// MYAC-1182 — attaching is JUST picking + uploading: the storage path carries no
// session segment, so no session is created here (creating one flipped the UI
// into "chat started" on a bare file drop — live finding, 2026-09-08). The
// session is created when the turn sends, as for any other message.
const handleAttachFiles = (files: File[]) => {
    const projectId = chatScopeId.value
    if (!projectId) return // the paperclip is disabled in this state; defensive no-op
    attachmentsStore.addAttachmentFiles(files, projectId)
}

const retryAttachment = (id: string) => {
    const projectId = chatScopeId.value
    if (!projectId) return
    attachmentsStore.retryAttachment(id, projectId)
}

// MYAC-1369 — drag-and-drop onto the chat. A dropped file takes the paperclip's
// exact path (`handleAttachFiles`): same checks, same chips, same retry. The
// drop is refused in the states that disable the paperclip, and says why.
const dropBlockedReason = computed<string | null>(() => {
    if (attachDisabled.value) return attachDisabledReason.value
    // MYAC-1269 — a reader of a shared conversation has nothing to attach to.
    if (chatbotStore.isReadOnlyConversation) return t('chat.shared.read_only')
    if (chatAwaitingPick.value) return t('chat.scope.attach-pick-first')
    if (isCreditsBlocked.value) return t('chat.insufficient-credits')
    if (chatbotStore.isLoading || chatbotStore.isStreaming) return t('chat.attachments.drop-wait')
    return null
})

// The limits come from the store's own constants, so the hint cannot advertise a
// ceiling the store no longer enforces.
const dropHint = computed(() => t('chat.attachments.drop-hint', {
    n: MAX_ATTACHMENTS_PER_TURN,
    mb: Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024)),
}, MAX_ATTACHMENTS_PER_TURN))

const isDraggingFiles = ref(false)
// dragenter/dragleave fire for every child crossed, so a boolean flips off the
// moment the pointer moves from the card onto a message inside it. Counting
// enters minus leaves is the usual cure: the drag has left the card at 0.
let dragDepth = 0

// Only a drag carrying files (from the desktop / a file manager) is ours;
// dragging selected text or a link across the chat must not raise the overlay.
const carriesFiles = (event: DragEvent): boolean =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files')

const onDragEnter = (event: DragEvent) => {
    if (!carriesFiles(event)) return
    event.preventDefault()
    dragDepth++
    isDraggingFiles.value = true
}

const onDragOver = (event: DragEvent) => {
    if (!carriesFiles(event)) return
    // Without preventDefault on dragover the browser never fires `drop` here.
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = dropBlockedReason.value ? 'none' : 'copy'
}

const onDragLeave = (event: DragEvent) => {
    if (!carriesFiles(event)) return
    dragDepth = Math.max(0, dragDepth - 1)
    // A child that unmounts mid-drag (the transcript re-rendering while a reply
    // streams) never fires its dragleave, so the counter alone can stick above 0
    // and leave the overlay covering the chat. Leaving for a node outside the card
    // settles it; a null relatedTarget (some browsers) falls back on the counter.
    const card = rootCard.value?.$el
    const next = event.relatedTarget as Node | null
    if (next && card && !card.contains(next)) dragDepth = 0
    if (dragDepth === 0) isDraggingFiles.value = false
}

const onDrop = (event: DragEvent) => {
    if (!carriesFiles(event)) return
    // Always swallow a file drop on the chat — even a refused one — or the
    // browser navigates away to open the file, losing the conversation.
    event.preventDefault()
    dragDepth = 0
    isDraggingFiles.value = false
    if (dropBlockedReason.value) return
    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length) handleAttachFiles(files)
}

const handleNewChat = async () => {
    // MYAC-1138: the only call site allowed to count as a user-initiated restart.
    // MYAC-918 — through the conversation move, so the conversation left behind
    // is kept in the person's list and they are told so (the mockup, PR 868).
    await startNewConversation()
    inputMessage.value = ''
    showRawEvents.value = false
    randomSuggestions.value = pickRandomSuggestions()
}


// MYAC-882 — the "Yes" retry choice on the error card. Credit-gated ($$$): retry
// fires a paid chat_message, same as typing.
const handleRetry = async () => {
    if (chatbotStore.isLoading || chatbotStore.isStreaming || isCreditsBlocked.value) {
        return
    }
    await chatbotStore.retryAfterError()
    // MYAC-1138: friction signal — fires once the retry actually goes out (past
    // the guard above), not on every click of a disabled/blocked button.
    try {
        trackAction(CsmAction.ChatRetried, { projectId: chatbotStore.projectId || undefined })
    } catch { /* tracking never breaks a retry */ }
}

// MYAC-882 — capability-driven example prompts (empty-session state + help popup).
// MYAC-835 — a context-scoped chat (e.g. the Product tab) may pass an ordered
// allow-list of group keys to lead with its own family and hide irrelevant ones.
const groupsByKey = new Map(CHAT_SUGGESTION_GROUPS.map(g => [g.key, g]))
const groupsFor = (keys: string[]): ChatSuggestionGroup[] =>
    keys.map(k => groupsByKey.get(k)).filter((g): g is ChatSuggestionGroup => g !== undefined)
const suggestionGroups = computed<ChatSuggestionGroup[]>(() =>
    props.suggestionGroupKeys?.length ? groupsFor(props.suggestionGroupKeys) : CHAT_SUGGESTION_GROUPS)
const showCapabilities = ref(false)
/** MYAC-1522 — the host page's spot for a letter's tools (letterTools.ts); none → in place. */
const letterToolsTarget = inject(LETTER_TOOLS_TARGET, ref(null))

// The empty state shows only a light sample (pickOpeningSuggestions has the rule).
// Re-rolled on each new session, and whenever the surface's scope changes — the
// docked panel stays mounted across navigations, so a chip set drawn on one page
// would otherwise follow the member onto the next.
const openingGroups = computed<ChatSuggestionGroup[]>(() =>
    props.openingGroupKeys?.length ? groupsFor(props.openingGroupKeys) : suggestionGroups.value)
const pickRandomSuggestions = (): ChatSuggestion[] =>
    pickOpeningSuggestions(openingGroups.value, {
        scoped: Boolean(props.openingGroupKeys?.length || props.suggestionGroupKeys?.length),
    })

const randomSuggestions = ref<ChatSuggestion[]>(pickRandomSuggestions())
// A string, not an array: the chat pages bind freshly built key arrays on every
// render, and a new array would re-roll the chips under a member reading them.
watch(
    () => `${props.suggestionGroupKeys?.join() ?? ''}|${props.openingGroupKeys?.join() ?? ''}`,
    () => { randomSuggestions.value = pickRandomSuggestions() },
)

const applySuggestion = async (suggestion: ChatSuggestion) => {
    // Credit-gated ($$$): a 'send' suggestion fires a paid chat_message, same as typing.
    if (suggestion.action === 'send' && isCreditsBlocked.value) {
        return
    }
    // Close the help popup so the user sees the conversation (or the pre-filled input).
    showCapabilities.value = false
    const prompt = t(suggestion.promptKey)
    if (suggestion.action === 'send') {
        await handleSend(prompt)
    } else {
        inputMessage.value = prompt
    }
}

const scrollToBottom = () => {
    const el = scrollContainer.value
    if (el) {
        el.scrollTop = el.scrollHeight
        // The auto-scroll moves the reader without a scroll event of their own.
        updateVisibleSubject()
    }
}

// Auto-scroll when new messages/events arrive (only when messages exist)
watch(() => chatbotStore.events.length, () => {
    if (!hasNoMessages.value) {
        nextTick(() => {
            scrollToBottom()
        })
    }
})

// Auto-scroll raw events view when events change
watch(() => chatbotStore.events, () => {
    if (showRawEvents.value && !hasNoMessages.value) {
        nextTick(() => {
            scrollToBottom()
        })
    }
}, { deep: true })

// Auto-scroll when error appears
watch(() => chatbotStore.error, () => {
    if (hasError.value) {
        nextTick(() => {
            scrollToBottom()
        })
    }
})

/**
 * Drop the conversation when the chat's scope changes — a project switch, or a
 * different project picked in the dashboard header (MYAC-1327).
 *
 * Gated on "is there a conversation to lose", NOT on `projectId !== null`. That
 * older guard skipped the one transition it most needed to catch: a conversation
 * held with NO scope (the pick state, and the create state) has
 * `chatbotStore.projectId === null`, so attaching it to a project left every
 * message on screen while `sendMessage` quietly opened a NEW server session
 * underneath — the model then answered "we have only just started" beneath a
 * visible thread it had never seen. The opening message made it worse: it renders
 * as turn zero whatever the message count, so the newly picked project's greeting
 * appeared ABOVE the previous scope's conversation.
 *
 * `events.length` is the right gate: on a cold mount there is nothing to lose, so
 * resolving a scope for the first time must not fire a reset (and must not stamp
 * a `chat_session_reset` on the way in — `resetSession()` is called without
 * `userInitiated`, so it stays out of the CSM counts either way, MYAC-1138).
 */
const checkAndResetProjectChat = async () => {
    const scopeId = chatScopeId.value
    const chatProjectId = chatbotStore.projectId

    if (chatProjectId !== scopeId && chatbotStore.events.length > 0) {
        logger.debug('Chat scope changed, resetting chat:', { chatProjectId, scopeId })
        await chatbotStore.resetSession()
        inputMessage.value = ''
    }
    // MYAC-1519 — follow the project's conversation this tab already had (reload).
    await chatbotStore.resumeProjectChat(scopeId || null)
}

// MYAC-1519 — resend a refused message (a paid turn: same gate as the box).
const handleResend = async (commandId?: string) => {
    if (!commandId || isCreditsBlocked.value || chatbotStore.isLoading || chatbotStore.isStreaming) return
    await chatbotStore.resendMessage(commandId)
}

// Approval cards scoped per turn (PR #274 review): while a reply streams, hold ONLY
// the cards born in this turn (event at/after streamingBaseEventIndex) — prior turns'
// pending cards stay visible instead of blinking out on every unrelated message.
const visibleApprovalProposals = computed(() => {
    const proposals = chatbotStore.approvalProposals
    if (!chatbotStore.isStreaming && !chatbotStore.isLoading) return proposals
    const indexById = new Map(chatbotStore.events.map((e, i) => [e.id, i]))
    return proposals.filter(p =>
        (indexById.get(p.eventId) ?? Infinity) < chatbotStore.streamingBaseEventIndex)
})

// MYAC-883 — staged register refreshes awaiting review. Subscribed here (not in the card)
// so a proposal from a run started in another session, or before a reload, still surfaces.
const registerProposalsStore = useRegisterProposalsStore()
const pendingProposals = computed(() =>
    registerProposalsStore.proposals.filter(p => !chatbotStore.resolvedProposalIds.includes(p.id)))

const subscribeToProposals = () => {
    const pid = chatScopeId.value
    if (pid) registerProposalsStore.subscribe(pid)
}

// Check on mount
onMounted(() => {
    const el = rootCard.value?.$el ?? null
    if (el && typeof ResizeObserver !== 'undefined') {
        hostObserver = new ResizeObserver(() => { hostWidth.value = el.clientWidth })
        hostObserver.observe(el)
        hostWidth.value = el.clientWidth
    }
    checkAndResetProjectChat()
    subscribeToProposals()
    void nextTick(updateVisibleSubject)
})

onBeforeUnmount(() => {
    hostObserver?.disconnect()
    hostObserver = null
})

// A new message changes what is under the reader without any scrolling of their
// own — recompute after it has been laid out.
watch(() => chatbotStore.messages.length, () => void nextTick(updateVisibleSubject))
// Attaching a subject makes it the current one — but only if the reader is
// actually at the bottom, so the position is re-measured, never assumed.
watch(() => chatbotStore.pendingFocus, () => void nextTick(updateVisibleSubject))

// Watch for scope changes while component is mounted (project switches AND
// dashboard QMS picks both flow through the one scope value — MYAC-1327)
watch(chatScopeId, () => {
    checkAndResetProjectChat()
    subscribeToProposals()
})

// MYAC-1106/1140 — this component only READS the saved prompts. The listener is
// owned by `useSavedPromptsSubscription()` in HomeView, because ChatInterface
// mounts more than once at a time (docked panel + full-page chat) and comes and
// goes with the panel, while the picker's prompts have to survive all of it.
</script>

<style scoped>
/* MYAC-1313 — `embedded`: the conversation's scrollbar is always in view, even
   before there is anything to scroll — the rail that ties the messages to the box
   under them (review 2026-09-14). Drawn by hand: a native overlay scrollbar (macOS
   "show when scrolling") would stay hidden. Blink/WebKit read the pseudo-elements;
   Firefox, which has none, gets the standard properties. */
.chat-scroll-always {
    overflow-y: scroll;
}

.chat-scroll-always::-webkit-scrollbar {
    width: 8px;
}

.chat-scroll-always::-webkit-scrollbar-track {
    background: var(--p-surface-100);
    border-radius: 9999px;
}

.chat-scroll-always::-webkit-scrollbar-thumb {
    background: var(--p-surface-300);
    border-radius: 9999px;
}

@supports not selector(::-webkit-scrollbar) {
    .chat-scroll-always {
        scrollbar-width: thin;
        scrollbar-color: var(--p-surface-300) var(--p-surface-100);
    }
}

/* MYAC-1609 — scrolled turns fade out over the top 32px instead of meeting a hard
   line. Two mask layers: the gradient over the content, a solid one over the
   scrollbar so the rail itself never fades. */
.chat-fade-top {
    mask-image: linear-gradient(to bottom, transparent, #000 32px), linear-gradient(#000, #000);
    mask-size: calc(100% - var(--chat-scrollbar-width, 0px)) 100%, var(--chat-scrollbar-width, 0px) 100%;
    mask-position: left top, right top;
    mask-repeat: no-repeat;
}

/* MYAC-1609 — a letter's text keeps its left edge on the page's title, dateline and
   composer (MYAC-1522), yet no line touches the scroll area's edges: the scroller
   reaches 12px further left and the reading column is inset by 12px on each side,
   on a measure widened by the same amount, so the text lands exactly where it did. */
.letter-inset {
    margin-left: -12px;
}
.letter-inset .letter-measure {
    padding-inline: 12px;
    max-width: calc(900px + 24px);
}
/* The composer ends where the text does: 12px short of the edge when the column is
   narrower than the measure, at the measure's end otherwise. */
.letter-footer {
    padding-right: 12px;
    max-width: calc(900px + 12px);
}
</style>
